import * as React from 'react';
import { Button } from '@material-ui/core';
import { httpClient } from '../utils/dataProvider';
import { ActionButton, Empty, MetricCard, PageHeader, Row, RowMeta, RowText, RowTitle, Search, StatusPill, Surface, Toolbar } from '../components/ui';

const DISPUTE_TABS = ['ALL', 'OPEN', 'RESOLVED'];
const linkStyle = { color: '#7c3aed', textDecoration: 'none', cursor: 'pointer', fontWeight: 600 };

export default function DisputeQueue() {
    const [cancellationDisputes, setCancellationDisputes] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [disputeTab, setDisputeTab] = React.useState('OPEN');
    const [view, setView] = React.useState('cancellations'); // 'cancellations' | 'noshow'
    const [search, setSearch] = React.useState('');
    const [cancellationDetail, setCancellationDetail] = React.useState(null);
    const [decideForm, setDecideForm] = React.useState({ decision: '', reason: '' });
    const [deciding, setDeciding] = React.useState(false);
    const [chatMessages, setChatMessages] = React.useState([]);
    const [chatLoading, setChatLoading] = React.useState(false);

    const load = () => {
        setLoading(true);
        // B5/B6 FIX: Use the new /disputes endpoint that returns ALL cancellation
        // disputes including resolved history (DISPUTED, ADMIN_DECIDED, UNILATERAL).
        // The dead DisputeCase system is no longer queried.
        httpClient('https://skilzapp.net/api/cancellationEvents/disputes').then(({ json }) => {
            setCancellationDisputes(Array.isArray(json) ? json : json.docs || []);
        }).catch(console.error).finally(() => setLoading(false));
    };

    React.useEffect(load, []);

    const loadChatMessages = (eventId) => {
        setChatLoading(true);
        setChatMessages([]);
        httpClient(`https://skilzapp.net/api/cancellationEvents/${eventId}/messages`).then(({ json }) => {
            setChatMessages(json.messages || []);
        }).catch(console.error).finally(() => setChatLoading(false));
    };

    const openCancellationDetail = (d) => {
        setCancellationDetail(d);
        loadChatMessages(d._id);
    };

    // ── Derived data ────────────────────────────────────────────────────────

    const isNoShow = (d) =>
        d.eventType === 'NO_SHOW_CLAIM' ||
        (d.disputeReason || '').toLowerCase().includes('no-show') ||
        (d.disputeReason || '').toLowerCase().includes('no show');

    const isOpen = (d) => d.responseStatus === 'DISPUTED';
    const isResolved = (d) => d.responseStatus === 'ADMIN_DECIDED' || d.responseStatus === 'UNILATERAL';

    const cancellationOnly = React.useMemo(() =>
        cancellationDisputes.filter(d => !isNoShow(d)),
        [cancellationDisputes]);

    const noShowOnly = React.useMemo(() =>
        cancellationDisputes.filter(d => isNoShow(d)),
        [cancellationDisputes]);

    const filterBySearch = (list) => {
        if (!search) return list;
        const q = search.toLowerCase();
        return list.filter(d =>
            (d.actor?.name || '').toLowerCase().includes(q) ||
            (d.counterparty?.name || '').toLowerCase().includes(q) ||
            (d.disputeReason || '').toLowerCase().includes(q)
        );
    };

    // B5/B6: Filter by tab — OPEN shows DISPUTED, RESOLVED shows ADMIN_DECIDED + UNILATERAL
    const filterByTab = (list) => {
        if (disputeTab === 'OPEN') return list.filter(isOpen);
        if (disputeTab === 'RESOLVED') return list.filter(isResolved);
        return list; // ALL
    };

    const disputeCounts = React.useMemo(() => ({
        ALL: cancellationOnly.length,
        OPEN: cancellationOnly.filter(isOpen).length,
        RESOLVED: cancellationOnly.filter(isResolved).length,
    }), [cancellationOnly]);

    const filteredCancellations = React.useMemo(() => filterByTab(filterBySearch(cancellationOnly)), [cancellationOnly, search, disputeTab]);
    const filteredNoShow = React.useMemo(() => filterByTab(filterBySearch(noShowOnly)), [noShowOnly, search, disputeTab]);

    // ── Actions ─────────────────────────────────────────────────────────────

    const handleDecide = (id) => {
        if (!decideForm.decision || !decideForm.reason) return;
        setDeciding(true);
        httpClient(`https://skilzapp.net/api/cancellationEvents/${id}/decide`, {
            method: 'POST',
            body: JSON.stringify(decideForm)
        }).then(() => { setCancellationDetail(null); setChatMessages([]); load(); setDecideForm({ decision: '', reason: '' }); })
            .catch(console.error).finally(() => setDeciding(false));
    };

    // ── Cancellation/No-Show dispute detail ─────────────────────────────────
    if (cancellationDetail) {
        const d = cancellationDetail;
        const hasResponse = !!d.disputeResponse;
        const deadlinePassed = d.disputeResponseDeadline && new Date() > new Date(d.disputeResponseDeadline);
        const isNoShowCase = isNoShow(d);

        return <main className="sk-page">
            <PageHeader
                eyebrow={isNoShowCase ? 'No-Show Dispute' : 'Cancellation Dispute'}
                title={`${d.actor?.name || 'Unknown'} vs ${d.counterparty?.name || 'Unknown'}`}
                description={`${d.eventType} · ${d.disputeReason || 'No reason'}`}
                actions={<ActionButton variant="secondary" onClick={() => { setCancellationDetail(null); setChatMessages([]); }}>← Back</ActionButton>}
            />

            {/* Summary metrics */}
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
                <MetricCard label="Type" value={isNoShowCase ? 'No-Show' : 'Cancellation'} detail={d.eventType} tone={isNoShowCase ? 'orange' : 'blue'} />
                <MetricCard label="Points" value={d.pointsAwarded || 0} detail="Currently awarded" />
                <MetricCard label="Status" value={d.responseStatus} detail={d.adminDecision ? `Decision: ${d.adminDecision}` : 'Pending'} tone={d.responseStatus === 'ADMIN_DECIDED' ? 'green' : 'warning'} />
            </div>

            {/* Parties with profile links */}
            <Surface title="Parties">
                <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 200, padding: 12, borderRadius: 12, background: '#fef2f2', border: '1px solid #fecaca' }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: '#991b1b', textTransform: 'uppercase', marginBottom: 4 }}>
                            {isNoShowCase ? 'Reported Party' : 'Canceller'}
                        </div>
                        <div style={{ fontSize: 16, fontWeight: 700 }}>
                            {d.actor?._id
                                ? <a href={`#/users/${d.actor._id}/show`} style={linkStyle}>{d.actor.name}</a>
                                : d.actor?.name || '—'}
                        </div>
                        <div style={{ fontSize: 13, color: '#6b7280' }}>{d.actor?.phone || '—'}</div>
                        <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>Role: {d.actorRole || '—'}</div>
                    </div>
                    <div style={{ flex: 1, minWidth: 200, padding: 12, borderRadius: 12, background: '#eff6ff', border: '1px solid #bfdbfe' }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: '#1e40af', textTransform: 'uppercase', marginBottom: 4 }}>
                            {isNoShowCase ? 'Reporting Party' : 'Counterparty'}
                        </div>
                        <div style={{ fontSize: 16, fontWeight: 700 }}>
                            {d.counterparty?._id
                                ? <a href={`#/users/${d.counterparty._id}/show`} style={linkStyle}>{d.counterparty.name}</a>
                                : d.counterparty?.name || '—'}
                        </div>
                        <div style={{ fontSize: 13, color: '#6b7280' }}>{d.counterparty?.phone || '—'}</div>
                    </div>
                </div>
            </Surface>

            {/* Cancellation details */}
            <Surface title="Event Details">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 14 }}>
                    <div><strong>Event Type:</strong> {d.eventType}</div>
                    <div><strong>Actor Role:</strong> {d.actorRole}</div>
                    <div><strong>Reason:</strong> {d.reason || '—'}</div>
                    <div><strong>Occurred:</strong> {d.occurredAt ? new Date(d.occurredAt).toLocaleString() : d.createdAt ? new Date(d.createdAt).toLocaleString() : '—'}</div>
                    <div><strong>Request:</strong> {d.request ? <a href={`#/requests/${d.request}/show`} style={linkStyle}>{d.request}</a> : '—'}</div>
                    <div><strong>Points Awarded:</strong> {d.pointsAwarded}</div>
                </div>
            </Surface>

            {/* Chatroom conversation */}
            <Surface title="Conversation History" description="Messages from the request chatroom — review context before deciding">
                {chatLoading && <div className="sk-state is-loading" role="status">Loading messages…</div>}
                {!chatLoading && !chatMessages.length && <Empty>No messages found for this request.</Empty>}
                {!chatLoading && chatMessages.length > 0 && (
                    <div style={{ maxHeight: 400, overflowY: 'auto', padding: '8px 0' }}>
                        {chatMessages.map((msg, i) => {
                            const isLog = msg.messageType === 'LOG';
                            return <div key={msg._id || i} style={{
                                padding: '8px 12px',
                                marginBottom: 6,
                                borderRadius: 10,
                                backgroundColor: isLog ? '#f3f4f6' : '#e0e7ff',
                                borderLeft: isLog ? '3px solid #9ca3af' : '3px solid #7c3aed',
                                fontSize: 13,
                            }}>
                                {!isLog && msg.sender && (
                                    <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 2, color: '#4c1d95' }}>
                                        {msg.sender._id
                                            ? <a href={`#/users/${msg.sender._id}/show`} style={{ ...linkStyle, fontSize: 12 }}>{msg.sender.name}</a>
                                            : msg.sender.name || 'Unknown'}
                                    </div>
                                )}
                                {isLog && <span style={{ fontSize: 11, color: '#6b7280', fontStyle: 'italic' }}>System: </span>}
                                <span>{msg.content}</span>
                                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                                    {msg.date ? new Date(msg.date).toLocaleString() : ''}
                                </div>
                            </div>;
                        })}
                    </div>
                )}
            </Surface>

            {/* Dispute claim */}
            <Surface title={isNoShowCase ? 'No-Show Report' : 'Dispute from Counterparty'}>
                <div style={{ background: '#fff3cd', padding: 12, borderRadius: 8, marginBottom: 12 }}>
                    <strong>{isNoShowCase ? 'Claim:' : 'Reason:'}</strong> {d.disputeReason || '—'}
                    {d.disputeExplanation && <p style={{ margin: '8px 0 0' }}>{d.disputeExplanation}</p>}
                    {d.disputeEvidence && <p style={{ margin: '8px 0 0', fontSize: 13 }}>
                        <strong>Evidence:</strong> <a href={d.disputeEvidence} target="_blank" rel="noreferrer">{d.disputeEvidence}</a>
                    </p>}
                </div>
            </Surface>

            {/* Canceller's response */}
            <Surface title={isNoShowCase ? 'Response from Reported Party' : "Canceller's Response"}>
                {hasResponse ? (
                    <div style={{ background: '#d1ecf1', padding: 12, borderRadius: 8 }}>
                        <p>{d.disputeResponse}</p>
                    </div>
                ) : deadlinePassed ? (
                    <div style={{ background: '#f8d7da', padding: 12, borderRadius: 8 }}>
                        <em>No response — deadline passed ({new Date(d.disputeResponseDeadline).toLocaleString()})</em>
                    </div>
                ) : (
                    <div style={{ background: '#e2e3e5', padding: 12, borderRadius: 8 }}>
                        <em>Waiting for response… Deadline: {d.disputeResponseDeadline ? new Date(d.disputeResponseDeadline).toLocaleString() : '—'}</em>
                    </div>
                )}
            </Surface>

            {/* Admin decision form */}
            {d.responseStatus !== 'ADMIN_DECIDED' && <Surface title="Admin Decision">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div>
                        <label style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Decision</label>
                        <select value={decideForm.decision} onChange={e => setDecideForm(f => ({ ...f, decision: e.target.value }))} style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '2px solid #a855f7', fontSize: 14, outline: 'none' }}>
                            <option value="">Select decision…</option>
                            <option value="AGREED">Agreed — Both at fault (1 pt on canceller)</option>
                            <option value="UNILATERAL">Unilateral — Unjustified cancellation (2 pts on canceller)</option>
                            <option value="DISMISSED">Dismissed — Dispute baseless (0 pts)</option>
                        </select>
                    </div>
                    <div>
                        <label style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Reason</label>
                        <textarea
                            value={decideForm.reason}
                            onChange={e => setDecideForm(f => ({ ...f, reason: e.target.value }))}
                            placeholder="Explain the decision rationale…"
                            rows={3}
                            style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '2px solid #a855f7', fontSize: 14, outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}
                        />
                    </div>
                    <Button
                        variant="contained"
                        disabled={deciding || !decideForm.decision || !decideForm.reason}
                        onClick={() => handleDecide(d._id)}
                        style={{ backgroundColor: '#7c3aed', color: '#fff', borderRadius: 12, padding: '10px 24px', fontWeight: 600, width: '100%' }}
                    >
                        {deciding ? 'Submitting…' : 'Submit Decision'}
                    </Button>
                </div>
            </Surface>}

            {/* Already decided */}
            {d.responseStatus === 'ADMIN_DECIDED' && <Surface title="Decision Made">
                <div style={{ background: '#d4edda', padding: 16, borderRadius: 12 }}>
                    <div style={{ marginBottom: 8 }}><strong>Decision:</strong> <StatusPill status={d.adminDecision === 'DISMISSED' ? 'active' : 'warning'}>{d.adminDecision}</StatusPill></div>
                    <div style={{ marginBottom: 8 }}><strong>Reason:</strong> {d.adminDecisionReason}</div>
                    <div><strong>Decided by:</strong> {d.adminDecidedBy?.name || d.adminDecidedBy || '—'} · {d.adminDecidedAt ? new Date(d.adminDecidedAt).toLocaleString() : '—'}</div>
                </div>
            </Surface>}
        </main>;
    }

    // ── Main list view ──────────────────────────────────────────────────────
    return <main className="sk-page">
        <PageHeader eyebrow="Operations" title="Disputes" description="Review and resolve disputes, cancellation conflicts, and no-show claims." />

        {/* Top-level view tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            <Button
                variant={view === 'cancellations' ? 'contained' : 'outlined'}
                onClick={() => setView('cancellations')}
                style={view === 'cancellations' ? { backgroundColor: '#7c3aed', color: '#fff' } : {}}
            >
                Cancellation Disputes ({cancellationOnly.length})
            </Button>
            <Button
                variant={view === 'noshow' ? 'contained' : 'outlined'}
                onClick={() => setView('noshow')}
                style={view === 'noshow' ? { backgroundColor: '#ef4444', color: '#fff' } : {}}
            >
                No-Show Disputes ({noShowOnly.length})
            </Button>
        </div>

        {/* Status filter tabs (ALL / OPEN / RESOLVED) */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
            {DISPUTE_TABS.map(t => <Button key={t} aria-pressed={disputeTab === t} variant={disputeTab === t ? 'contained' : 'outlined'} color="primary" size="small" onClick={() => setDisputeTab(t)}>{t === 'ALL' ? 'All' : t} ({disputeCounts[t]})</Button>)}
        </div>

        {/* ── Cancellation Disputes ──────────────────────────────────────── */}
        {view === 'cancellations' && <>
            <Surface title="Cancellation Disputes" description={`${filteredCancellations.length} disputes — ${disputeTab === 'RESOLVED' ? 'resolved history' : disputeTab === 'OPEN' ? 'pending decisions' : 'all'}`}>
                <Toolbar right={<Search value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or reason…" />} />
                {loading && <div className="sk-state is-loading" role="status">Loading…</div>}
                {!loading && !filteredCancellations.length && <Empty>No cancellation disputes match these filters.</Empty>}
                {filteredCancellations.map(d => <CancellationRow key={d._id} d={d} onClick={() => openCancellationDetail(d)} />)}
            </Surface>
        </>}

        {/* ── No-Show Disputes ───────────────────────────────────────────── */}
        {view === 'noshow' && <>
            <Surface title="No-Show Disputes" description={`${filteredNoShow.length} no-show disputes — ${disputeTab === 'RESOLVED' ? 'resolved history' : disputeTab === 'OPEN' ? 'pending' : 'all'}`}>
                <Toolbar right={<Search value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name…" />} />
                {loading && <div className="sk-state is-loading" role="status">Loading…</div>}
                {!loading && !filteredNoShow.length && <Empty>No no-show disputes match these filters.</Empty>}
                {filteredNoShow.map(d => <CancellationRow key={d._id} d={d} onClick={() => openCancellationDetail(d)} isNoShow />)}
            </Surface>
        </>}
    </main>;
}

// ── Reusable row component for cancellation/no-show disputes ────────────────
function CancellationRow({ d, onClick, isNoShow }) {
    const hasResponse = !!d.disputeResponse;
    const deadlinePassed = d.disputeResponseDeadline && new Date() > new Date(d.disputeResponseDeadline);
    const responseLabel = hasResponse
        ? '✓ Response received'
        : deadlinePassed
            ? '✗ No response (expired)'
            : '⏳ Awaiting response';

    return <Row right={
        <div style={{ textAlign: 'right' }}>
            <StatusPill status={d.responseStatus === 'ADMIN_DECIDED' ? 'active' : 'warning'}>
                {d.responseStatus === 'ADMIN_DECIDED' ? d.adminDecision : d.responseStatus}
            </StatusPill>
            <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>{responseLabel}</div>
        </div>
    }>
        <RowText>
            <RowTitle>
                <a href="#" onClick={e => { e.preventDefault(); onClick(); }} style={{ color: '#7c3aed', textDecoration: 'none', fontWeight: 600 }}>
                    {d.actor?.name || '—'} vs {d.counterparty?.name || '—'}
                </a>
                {isNoShow && <span style={{ marginLeft: 8, padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600, background: '#fecaca', color: '#991b1b' }}>NO-SHOW</span>}
            </RowTitle>
            <RowMeta>
                {d.disputeReason || 'No reason'} · {d.eventType}
                {d.request && <> · Request: <a href={`#/requests/${d.request}/show`} style={{ color: '#7c3aed', textDecoration: 'none', fontSize: 12 }}>{String(d.request).slice(-8)}</a></>}
            </RowMeta>
            <RowMeta dim>{d.createdAt ? new Date(d.createdAt).toLocaleDateString() : '—'}</RowMeta>
        </RowText>
    </Row>;
}
